import Seo, { breadcrumbs, webPageSchema, serviceSchema } from "../components/Seo";
import LeadForm from "../components/LeadForm";
import CtaCallout from "../components/CtaCallout";
import { SectionHeader } from "../components/UI";
import AboutScottBlurb from "../components/AboutScottBlurb";
import { useState } from "react";

const CHIPS = ["New","Used","Either","Truck","SUV","EV","AWD","Under $25k","Low Payment","Lease","Finance","Trade-In"];

export default function FindMeVehicle() {
  const [chips, setChips] = useState([]);
  const toggle = (c) => setChips((arr) => arr.includes(c) ? arr.filter((x) => x !== c) : [...arr, c]);

  return (
    <main id="main-content">
      <Seo
        title="Find Me a Vehicle in Central Ohio | Scott Sells Chevys"
        description="Tell Scott what you want and let him do the searching. Share new vs used, budget, must-haves, and trade — Scott will send back real options in Central Ohio."
        canonicalPath="/find-me-a-vehicle"
        jsonLd={[
          breadcrumbs([{ name: "Home", path: "/" }, { name: "Find Me a Vehicle", path: "/find-me-a-vehicle" }]),
          webPageSchema({ name: "Find Me a Vehicle", path: "/find-me-a-vehicle", description: "Personal vehicle-finder request handled by Scott Hall." }),
          serviceSchema({
            name: "Personal Vehicle Finder",
            serviceType: "Vehicle shopping assistance",
            path: "/find-me-a-vehicle",
            description: "Scott reviews what you are looking for and sends back real options that fit your budget, body style, and timing.",
          }),
        ]}
      />
      <section className="ssc-hero">
        <div className="ssc-container py-10 md:py-16">
          <h1 className="ssc-display text-3xl md:text-5xl font-extrabold">Tell Me What You Want. I'll Help Find It.</h1>
          <p className="mt-3 text-[var(--ssc-silver)] max-w-2xl">Tell me what you need, what you want to spend, and what matters most. I'll help narrow down real options from available inventory.</p>
        </div>
      </section>
      <section className="ssc-section">
        <div className="ssc-container max-w-3xl">
          <SectionHeader title="What are you looking for?" />
          <AboutScottBlurb
            heading="Not Sure What Fits? That's Where I Can Help."
            text="After more than 15 years in the car business, I know most shoppers do not always start with the perfect vehicle in mind. Tell me what you need, what you want to spend, and what matters most. I'll help you narrow it down."
            testId="finder-about-scott"
            className="my-6"
          />
          <div className="flex flex-wrap gap-2 mb-6">
            {CHIPS.map((c) => (
              <button key={c} type="button" onClick={() => toggle(c)} className={`ssc-chip ${chips.includes(c) ? "ssc-chip-active" : ""}`} data-testid={`chip-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {c}
              </button>
            ))}
          </div>
          <LeadForm
            leadType="find_me_vehicle"
            leadSource="Find Me a Vehicle Page"
            testIdPrefix="find-lead"
            fields={[
              { name: "name", label: "Name", required: true, autoComplete: "name" },
              { name: "phone", label: "Phone", inputMode: "tel", autoComplete: "tel" },
              { name: "email", label: "Email", type: "email", autoComplete: "email" },
              { name: "preferred_contact", label: "Preferred Contact", type: "select", options: ["Text", "Call", "Email"] },
              { name: "new_or_used", label: "New, Used, or Either", type: "select", options: ["New", "Used", "Either"] },
              { name: "vehicle_type", label: "Vehicle Type", type: "select", options: ["Truck", "SUV", "Car", "EV", "Not Sure"] },
              { name: "preferred_models", label: "Preferred Models" },
              { name: "budget", label: "Budget or Payment Goal" },
              { name: "cash_down", label: "Cash Down (if any)" },
              { name: "trade_in", label: "Trade-In?", type: "select", options: ["Yes", "No"] },
              { name: "current_vehicle", label: "Current Vehicle" },
              { name: "payoff_amount", label: "Payoff Amount (if known)" },
              { name: "must_haves", label: "Must-Have Features", full: true },
              { name: "nice_to_haves", label: "Nice-to-Have Features", full: true },
              { name: "timeline", label: "Timeline", type: "select", options: ["Within a week", "1–4 weeks", "1–3 months", "Just planning"] },
              { name: "best_time", label: "Best Time to Contact" },
              { name: "message", label: "Message", type: "textarea", full: true },
            ]}
            hidden={{ chips_selected: chips.join(",") }}
            submitLabel="Send to Scott"
            successMessage="Thanks. I'll review what you're looking for and follow up with options that make sense."
          />
          <CtaCallout
            testId="find-cta-callout"
            heading="Not ready to fill the form?"
            sub="Text Scott directly or ask about payment or trade — no pressure."
            actions={[
              { key: "text", label: "Text Scott", icon: "text", href: "sms:+16145358673" },
              { key: "payment", label: "Payment Help", icon: "payment", to: "/payment-help" },
              { key: "trade", label: "Value My Trade", icon: "trade", to: "/value-my-trade" },
              { key: "ask", label: "Ask Scott", icon: "ask", to: "/contact" },
            ]}
          />
        </div>
      </section>
    </main>
  );
}
